import { inject, Injectable } from '@angular/core';
import { HxAuthService } from 'hx-services';

/** What is kept about a person looked at: the id and the name for the chip, nothing else (no phone). */
export interface RecentPerson { id: number; name: string }

const KEY = 'adm.access.recent-people';
const LIMIT = 3;

/**
 * «Недавние» on the tab «Сотрудник»: the last people looked at, kept in the browser under the key of the admin who looked, and wiped when he logs out
 * (`clear()`, called by the menu «Выход»). Only the id and the name are kept — the card of the person is read again when he is picked.
 */
@Injectable({ providedIn: 'root' })
export class AccessRecentService {
  private readonly auth = inject(HxAuthService);

  read(): RecentPerson[] {
    try {
      const raw = window.localStorage.getItem(this.key());
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) {
        return [];
      }
      return (parsed as Partial<RecentPerson>[])
        .filter((u): u is RecentPerson => typeof u?.id === 'number' && typeof u?.name === 'string')
        .map(u => ({ id: u.id, name: u.name }))
        .slice(0, LIMIT);
    } catch {
      return [];
    }
  }

  remember(user: { id: number; name: string }): RecentPerson[] {
    const next = [{ id: user.id, name: user.name }, ...this.read().filter(u => u.id !== user.id)].slice(0, LIMIT);
    try {
      window.localStorage.setItem(this.key(), JSON.stringify(next));
    } catch {
      // the browser may forbid the storage; the list is just not kept then
    }
    return next;
  }

  /** Forgets everybody: called on logout, so the next person at the same computer sees nothing. Also removes the lists of every admin and the older key with phones. */
  clear(): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const name = window.localStorage.key(i);
        if (name !== null && (name === KEY || name.startsWith(`${KEY}.`))) {
          keys.push(name);
        }
      }
      keys.forEach(name => window.localStorage.removeItem(name));
    } catch {
      // nothing to wipe when the storage is closed
    }
  }

  private key(): string {
    const id = (this.auth as { user?: { id?: number } }).user?.id;
    return id === undefined ? KEY : `${KEY}.${id}`;
  }
}
